import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, CheckCircle, Users, GraduationCap, MapPin, DollarSign, FileText } from 'lucide-react';
import axios from 'axios';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CounselingRequest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_mobile: '',
    jee_rank: '',
    category: 'General',
    preferred_branches: [],
    preferred_states: [],
    budget: '',
    additional_notes: ''
  });

  const [branchInput, setBranchInput] = useState('');
  const [stateInput, setStateInput] = useState('');

  const categories = ['General', 'OBC', 'SC', 'ST', 'EWS'];
  const popularBranches = [
    'Computer Science Engineering',
    'Electronics and Communication',
    'Mechanical Engineering',
    'Electrical Engineering',
    'Civil Engineering',
    'Chemical Engineering',
    'Information Technology',
    'Artificial Intelligence',
    'Data Science'
  ];

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal'
  ];

  useEffect(() => {
    // Load user data from localStorage
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('userEmail');
    const userMobile = localStorage.getItem('userMobile');
    
    if (!userId) {
      toast({
        title: "Quick sign-in needed!",
        description: "Create your free account to connect with our expert counselors",
        variant: "default"
      });
      navigate('/user-login');
      return;
    }

    setFormData(prev => ({
      ...prev,
      user_name: userName || '',
      user_email: userEmail || '',
      user_mobile: userMobile || ''
    }));
  }, [navigate]);

  const handleAddBranch = () => {
    if (branchInput.trim() && !formData.preferred_branches.includes(branchInput.trim())) {
      setFormData({
        ...formData,
        preferred_branches: [...formData.preferred_branches, branchInput.trim()]
      });
      setBranchInput('');
    }
  };

  const handleRemoveBranch = (branch) => {
    setFormData({
      ...formData,
      preferred_branches: formData.preferred_branches.filter(b => b !== branch)
    });
  };

  const handleAddState = () => {
    if (stateInput.trim() && !formData.preferred_states.includes(stateInput.trim())) {
      setFormData({
        ...formData,
        preferred_states: [...formData.preferred_states, stateInput.trim()]
      });
      setStateInput('');
    }
  };

  const handleRemoveState = (state) => {
    setFormData({
      ...formData,
      preferred_states: formData.preferred_states.filter(s => s !== state)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.jee_rank || formData.jee_rank <= 0) {
      toast({
        title: "Invalid Rank",
        description: "Please enter a valid JEE rank",
        variant: "destructive"
      });
      return;
    }

    if (!formData.user_mobile || formData.user_mobile.length < 10) {
      toast({
        title: "Invalid Mobile",
        description: "Please enter a valid 10-digit mobile number",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const userId = localStorage.getItem('userId');
      const requestData = {
        user_id: userId,
        user_name: formData.user_name,
        user_email: formData.user_email,
        user_mobile: formData.user_mobile,
        jee_rank: parseInt(formData.jee_rank),
        category: formData.category,
        preferred_branches: formData.preferred_branches,
        preferred_states: formData.preferred_states,
        budget: formData.budget,
        additional_notes: formData.additional_notes,
        status: 'pending'
      };

      const response = await axios.post(`${BACKEND_URL}/api/counseling/request`, requestData);

      if (response.data.success) {
        setSubmitted(true);
        toast({
          title: "Success!",
          description: "Your counseling request has been submitted successfully. Our team will contact you soon.",
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Counseling request error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto text-center">
            <CardContent className="pt-12 pb-12">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4">Request Submitted Successfully!</h2>
              <p className="text-slate-600 mb-8">
                Thank you for requesting counseling. Our expert counselors will review your information and contact you within 24-48 hours.
              </p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/')}>
                  Back to Home
                </Button>
                <Button variant="outline" onClick={() => navigate('/my-predictions')}>
                  View My Predictions
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
            <h1 className="text-xl font-bold text-slate-900">Request Counseling</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Info Card */}
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-blue-600" />
              Get Expert College Counseling
            </CardTitle>
            <CardDescription>
              Our experienced counselors will help you make the best college choice based on your JEE rank, preferences, and career goals.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Counseling Request Form</CardTitle>
            <CardDescription>
              Fill in your details and our counselors will reach out to you
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.user_name}
                      onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.user_email}
                      onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile Number *</Label>
                    <Input
                      id="mobile"
                      type="tel"
                      value={formData.user_mobile}
                      onChange={(e) => setFormData({ ...formData, user_mobile: e.target.value })}
                      required
                      placeholder="10-digit mobile number"
                      pattern="[0-9]{10}"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Academic Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rank">JEE Rank *</Label>
                    <Input
                      id="rank"
                      type="number"
                      value={formData.jee_rank}
                      onChange={(e) => setFormData({ ...formData, jee_rank: e.target.value })}
                      required
                      placeholder="Enter your JEE rank"
                      min="1"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full p-2 border rounded-md"
                      required
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Preferences */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Your Preferences
                </h3>
                
                {/* Preferred Branches */}
                <div className="space-y-2">
                  <Label>Preferred Branches</Label>
                  <div className="flex gap-2">
                    <select
                      value={branchInput}
                      onChange={(e) => setBranchInput(e.target.value)}
                      className="flex-1 p-2 border rounded-md"
                    >
                      <option value="">Select a branch</option>
                      {popularBranches.map(branch => (
                        <option key={branch} value={branch}>{branch}</option>
                      ))}
                    </select>
                    <Button type="button" onClick={handleAddBranch} variant="outline">
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.preferred_branches.map(branch => (
                      <Badge key={branch} variant="secondary" className="px-3 py-1">
                        {branch}
                        <button
                          type="button"
                          onClick={() => handleRemoveBranch(branch)}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Preferred States */}
                <div className="space-y-2">
                  <Label>Preferred States</Label>
                  <div className="flex gap-2">
                    <select
                      value={stateInput}
                      onChange={(e) => setStateInput(e.target.value)}
                      className="flex-1 p-2 border rounded-md"
                    >
                      <option value="">Select a state</option>
                      {indianStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    <Button type="button" onClick={handleAddState} variant="outline">
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.preferred_states.map(state => (
                      <Badge key={state} variant="secondary" className="px-3 py-1">
                        {state}
                        <button
                          type="button"
                          onClick={() => handleRemoveState(state)}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Budget */}
                <div className="space-y-2">
                  <Label htmlFor="budget" className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Annual Budget (Optional)
                  </Label>
                  <Input
                    id="budget"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="e.g., ₹2-3 Lakhs per year"
                  />
                </div>
              </div>

              {/* Additional Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  id="notes"
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                  placeholder="Any specific requirements or questions..."
                  rows={4}
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <Button 
                  type="submit" 
                  className="flex-1" 
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigate('/')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default CounselingRequest;
